<template>
    <div class="af-search-result-map">
        <SearchNoResults v-if="!jobs.length"/>
        <div class="mapview-wrapper" v-else>
            <div class="mapview-search-address afu-my-4">
                <div class="afu-flex">
                    <C1FormInput
                        type="text"
                        ref="mapViewLocation"
                        v-model="searchAddress"
                        class="afu-flex-grow"
                        :placeholder="$t('search_results.mapViewLocation')"
                    />

                    <C1FormInput
                        type="text"
                        class="afu-mx-1"
                        v-model="searchRadius"
                        @change="this.updateMap"
                        :placeholder="$t('search_results.mapViewRadius')"
                    />

                    <C1Button
                        type="button"
                        variant="neutral"
                        size="sm"
                        @click="this.updateMap"
                    >
                        {{ $t('search_results.mapViewUpdate') }}
                    </C1Button>

                    <C1Button
                        type="button"
                        variant="neutral"
                        size="sm"
                        @click="this.resetMap"
                    >
                        {{ $t('search_results.mapViewReset') }}
                    </C1Button>
                </div>
            </div>

            <div ref="searchResultMap" :style="{width: '100%', height: this.height}"/>
        </div>
    </div>
</template>

<script>
    import Filters from '@/components/Utils/Filters';
    import SearchNoResults from './SearchNoResults';
    import {mapMutations} from 'vuex';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Button from '@/components/C1Components/C1Button';

    import {getJobPayMinMaxLabel, getJobViewLink, loadMapScripts} from '@/utils/helpers';

    export default {
        name: 'SearchResultMap',

        components: {
            SearchNoResults,
            C1FormInput,
            C1Button,
        },

        mixins: [Filters],

        props: {
            jobs: {
                type: Array,
                default: () => [],
            },
            height: {
                type: String,
                default: () => '640px',
            },
        },

        data() {
            return {
                map: null,
                markers: [],
                jobsGroup: [],
                markerClusterer: null,
                markerInfoWindow: null,
                searchAddress: null,
                searchLocation: null,
                searchRadius: null,
                searchZoom: 18,
                centerLocation: {lat: -25.2744, lng: 133.7751},
                centerZoom: 4,
            };
        },

        computed: {
            linkTarget() {
                return window?.afConfig?.jobs_list_map_link_target || '_blank';
            },
        },

        methods: {
            ...mapMutations('search', {
                $_SET_MAPVIEW_JOBS_TOTAL: 'SET_MAPVIEW_JOBS_TOTAL',
            }),

            showJobInfoWindow(marker) {
                const idx = this.getMarkerPositionKey(marker);

                const jobs = this.jobsGroup[idx];
                if (!jobs) {
                    return;
                }

                if (!this.markerInfoWindow) {
                    const gMap = this.getGMapInstance();

                    this.markerInfoWindow = new gMap.InfoWindow({
                        content: '',
                        autoPan: false,
                    });
                }

                const items = [];

                jobs.forEach((job) => {
                    const jobUrl = getJobViewLink(job.URL);

                    const classifications = [];
                    if (job.classification_relationship && Array.isArray(job.classification_relationship)) {
                        for (const classification of job.classification_relationship) {
                            for (const subClassification of classification.subclassifications) {
                                classifications.push(`
                                    <div class="mapview-info-classification">
                                        ${classification.value} &gt; ${subClassification.value}
                                    </div>
                                `);
                            }
                        }
                    }

                    let payDetails = '';
                    if (!parseInt(job.pay_is_hidden)) {
                        payDetails = getJobPayMinMaxLabel(job, this.$t('global.currency_symbol'));
                    }

                    items.push(`
                        <div class="mapview-info-job">
                            <h3 class="mapview-info-job-title afu-m-0 afu-mb-1">
                                <a href="${jobUrl}" class="afu-text-black afu-font-semibold afu-no-underline" target="${this.linkTarget}">${job.job_title}</a>
                            </h3>
                            <div class="mapview-info-location">${job.location_label}</div>
                            <div class="mapview-info-classifications">${classifications.join('')}</div>
                            <div class="mapview-info-pay">${payDetails}</div>
                        </div>
                    `);
                });

                this.markerInfoWindow.setContent(`
                    <div class="mapview-info">
                        ${items.join('<hr class="afu-border-0 afu-border-t afu-border-neutral afu-my-3">')}
                    </div>
                `);

                this.markerInfoWindow.open({
                    anchor: marker,
                    map: marker.map,
                    shouldFocus: true,
                });
            },

            initialiseMap() {
                if (this.jobs.length === 0) {
                    this.$_SET_MAPVIEW_JOBS_TOTAL(this.markers.length);

                    return;
                }

                this.setupAutoComplete();

                const gMap = this.getGMapInstance();

                const map = new gMap.Map(this.$refs.searchResultMap, {
                    center: this.centerLocation,
                    zoom: this.centerZoom,
                });

                if (this.markerClusterer) {
                    this.markerClusterer.clearMarkers();
                }

                if (this.markers.length !== 0) {
                    for (const marker of this.markers) {
                        marker.setMap(null);
                    }
                }

                const markers = [];
                const jobsGroup = {};

                for (const job of this.jobs) {
                    if (job.latitude && job.longitude) {
                        const latLng = {
                            lat: parseFloat(job.latitude),
                            lng: parseFloat(job.longitude),
                        };

                        const marker = new gMap.Marker({
                            map,
                            position: latLng,
                            title: job.job_title,
                        });

                        const idx = this.getMarkerPositionKey(marker);
                        if (!jobsGroup[idx]) {
                            jobsGroup[idx] = [];
                        }
                        jobsGroup[idx].push(job);

                        marker.addListener('click', () => {
                            this.showJobInfoWindow(marker);
                        });

                        markers.push(marker);
                    }
                }

                this.map = map;
                this.markers = markers;
                this.jobsGroup = jobsGroup;

                this.$_SET_MAPVIEW_JOBS_TOTAL(markers.length);

                this.buildMarkerCluster();

                this.fitMapToBounds();

                gMap.event.addListener(map, 'click', () => {
                    this.markerInfoWindow?.close();
                });

                gMap.event.addListener(this.markerClusterer, 'click', () => {
                    this.markerInfoWindow?.close();
                });
            },

            setupAutoComplete() {
                const gMap = this.getGMapInstance();

                const autocomplete = new gMap.places.Autocomplete(
                    this.$refs.mapViewLocation.$refs.input,
                    {types: ['geocode']});

                gMap.event.addListener(autocomplete, 'place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (!place) {
                        return;
                    }

                    this.searchAddress = place.formatted_address;
                    this.searchLocation = place.geometry.location;

                    this.updateMap();
                });
            },

            resetMap() {
                this.searchAddress = null;
                this.searchLocation = null;
                this.searchRadius = null;

                this.updateMap();
            },

            updateMap() {
                if (!this.map) {
                    return;
                }

                this.markerInfoWindow?.close();

                if (!this.searchLocation) {
                    this.showAllMarkers();

                    return;
                }

                const gMap = this.getGMapInstance();

                const radius = this.searchRadius ? this.searchRadius * 1000 : 10000;

                this.markers.forEach((marker) => {
                    const dist = gMap.geometry.spherical.computeDistanceBetween(this.searchLocation, marker.getPosition());

                    marker.setVisible(radius >= dist);
                });

                this.buildMarkerCluster();

                this.fitMapToBounds();
            },

            showAllMarkers() {
                if (!this.map) {
                    return;
                }

                this.markers.forEach((marker) => {
                    marker.setVisible(true);
                });

                this.buildMarkerCluster();

                this.fitMapToBounds();
            },

            buildMarkerCluster() {
                if (this.markerClusterer) {
                    this.markerClusterer.clearMarkers();
                    this.markerClusterer.setMap(null);
                }

                const markers = [];

                this.markers.forEach((marker) => {
                    if (marker.getVisible()) {
                        markers.push(marker);
                    }
                });

                // eslint-disable-next-line no-undef
                this.markerClusterer = new markerClusterer.MarkerClusterer({
                    map: this.map,
                    markers: markers,
                });
            },

            fitMapToBounds() {
                const gMap = this.getGMapInstance();

                const bounds = new gMap.LatLngBounds();

                let visible = 0;

                this.markers.forEach((marker) => {
                    if (marker.getVisible()) {
                        bounds.extend(marker.getPosition());

                        visible++;
                    }
                });

                this.$_SET_MAPVIEW_JOBS_TOTAL(visible);

                if (visible) {
                    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
                        const offset = 0.002;
                        const center = bounds.getCenter();
                        bounds.extend(new gMap.LatLng(center.lat() + offset, center.lng() + offset));
                        bounds.extend(new gMap.LatLng(center.lat() - offset, center.lng() - offset));
                    }

                    this.map.fitBounds(bounds);
                    this.map.setCenter(bounds.getCenter());
                } else if (this.searchLocation) {
                    this.map.setCenter(this.searchLocation);
                    this.map.setZoom(this.searchZoom);
                } else {
                    this.map.setCenter(this.centerLocation);
                    this.map.setZoom(this.centerZoom);
                }
            },

            getMarkerPositionKey(marker) {
                return marker.position.lat() + '-' + marker.position.lng();
            },

            getGMapInstance() {
                // eslint-disable-next-line no-undef
                return google.maps;
            },
        },

        mounted() {
            loadMapScripts(this.initialiseMap, true);
        },

        beforeDestroy() {
            if (this.markerClusterer) {
                this.markerClusterer.clearMarkers();
                this.markerClusterer.setMap(null);
            }

            this.markers.forEach((marker) => {
                marker.setMap(null);
            });
        },
    };
</script>
